import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Compass, ArrowRight, Sparkles, Eye, EyeOff, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import api, { setAuthToken } from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

export const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();

  // Detect in-app browser (Substack, Instagram, Facebook, Twitter, etc.)
  const isInAppBrowser = (() => {
    const ua = navigator.userAgent || '';
    return /FBAN|FBAV|Instagram|Substack|Twitter|Line|Snapchat|Pinterest/i.test(ua) ||
      (ua.includes('Mobile') && !ua.includes('Safari') && ua.includes('AppleWebKit'));
  })();

  // Fetch Google Client ID on mount
  useEffect(() => {
    const fetchGoogleClientId = async () => {
      try {
        console.log('Fetching Google Client ID...');
        const response = await api.get('/auth/google/client-id');
        console.log('Google Client ID received:', response.data.client_id ? 'yes' : 'no');
        setGoogleClientId(response.data.client_id);
      } catch (error) {
        console.error('Failed to fetch Google Client ID:', error);
        toast.error('Google sign-in temporarily unavailable');
      }
    };
    fetchGoogleClientId();
  }, []);

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(async (code) => {
    console.log('Google callback received, exchanging code...');
    setGoogleLoading(true);
    try {
      const redirectUri = window.location.origin;
      console.log('Exchanging code with redirect_uri:', redirectUri);
      const response = await api.post('/auth/google/exchange', {
        code: code,
        redirect_uri: redirectUri
      });
      console.log('Exchange successful, setting token');
      setAuthToken(response.data.token, rememberMe);
      toast.success(language === 'tr' ? 'Google ile giriş başarılı!' : 'Signed in with Google!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Google exchange failed:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Google authentication failed');
    } finally {
      setGoogleLoading(false);
    }
  }, [navigate, language, rememberMe]);

  // Check for OAuth callback code in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      // Clear the URL parameters
      window.history.replaceState({}, document.title, '/auth');
      handleGoogleCallback(code);
    }
    
    // Show error message if redirected from failed OAuth
    if (location.state?.error) {
      toast.error(location.state.error);
    }
  }, [location.state, handleGoogleCallback]);

  const handleGoogleSignIn = () => {
    console.log('Google sign-in clicked, clientId:', googleClientId ? 'loaded' : 'not loaded');
    if (!googleClientId) {
      toast.error(language === 'tr' ? 'Google girişi yükleniyor, tekrar deneyin' : 'Google sign-in loading, please try again');
      return;
    }
    
    const redirectUri = window.location.origin;
    const scope = 'email profile';
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
    
    console.log('Redirecting to Google:', googleAuthUrl.substring(0, 100) + '...');
    window.location.href = googleAuthUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await api.post(endpoint, payload);
      setAuthToken(response.data.token, rememberMe);
      toast.success(isLogin ? t('auth.welcomeBack') : t('auth.createAccount'));
      navigate(isLogin ? '/dashboard' : '/welcome');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error(language === 'tr' ? 'Lütfen e-posta adresinizi girin' : 'Please enter your email');
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotPasswordEmail });
      setForgotPasswordSent(true);
      toast.success(language === 'tr' ? 'Şifre sıfırlama bağlantısı gönderildi!' : 'Password reset link sent!');
    } catch (error) {
      // We still show success for security (prevent email enumeration)
      setForgotPasswordSent(true);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const content = {
    en: {
      tagline: 'Inner Navigation',
      subtitle: 'Discover your path. Align your life.',
      welcomeBack: 'Welcome back',
      beginJourney: 'Begin your journey',
      googleCta: 'Continue with Google',
      orDivider: 'or continue with email',
      signIn: 'Sign In',
      createAccount: 'Create Account',
      needAccount: 'Need an account? Sign up',
      haveAccount: 'Have an account? Sign in',
      privacy: 'Privacy Policy',
      forgotPassword: 'Forgot password?',
      forgotPasswordTitle: 'Reset Password',
      forgotPasswordDesc: 'Enter your email and we\'ll send you a reset link',
      sendResetLink: 'Send Reset Link',
      backToLogin: 'Back to login',
      emailSent: 'Check your email',
      emailSentDesc: 'We\'ve sent a password reset link to your email. Please check your inbox (and spam folder).',
      keepSignedIn: 'Keep me signed in',
    },
    tr: {
      tagline: 'İç Navigasyon',
      subtitle: 'Yolunu keşfet. Hayatını hizala.',
      welcomeBack: 'Tekrar hoş geldin',
      beginJourney: 'Yolculuğuna başla',
      googleCta: 'Google ile devam et',
      orDivider: 'veya e-posta ile devam et',
      signIn: 'Giriş Yap',
      createAccount: 'Hesap Oluştur',
      needAccount: 'Hesabın yok mu? Kayıt ol',
      haveAccount: 'Hesabın var mı? Giriş yap',
      privacy: 'Gizlilik Politikası',
      forgotPassword: 'Şifreni mi unuttun?',
      forgotPasswordTitle: 'Şifre Sıfırla',
      forgotPasswordDesc: 'E-postanı gir, sana sıfırlama bağlantısı gönderelim',
      sendResetLink: 'Sıfırlama Bağlantısı Gönder',
      backToLogin: 'Girişe dön',
      emailSent: 'E-postanı kontrol et',
      emailSentDesc: 'Şifre sıfırlama bağlantısını e-postana gönderdik. Lütfen gelen kutunu (ve spam klasörünü) kontrol et.',
      keepSignedIn: 'Oturumumu açık tut',
    },
  };

  const c = content[language] || content.en;

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center px-6 py-12">
      {/* Background subtle radial gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 via-transparent to-transparent pointer-events-none" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Header with Sun Icon */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/30"
          >
            <Compass className="w-10 h-10 text-[#D4AF37]" />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="font-serif font-light text-4xl text-[#F8F5F2] mb-2">
              IN
            </h1>
            <p className="font-sans text-[#D4AF37] text-lg tracking-wide">
              {c.tagline}
            </p>
            <p className="font-sans text-[#A1A1AA] text-sm mt-2">
              {c.subtitle}
            </p>
          </motion.div>
        </div>

        {/* Auth Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-panel rounded-2xl p-8"
          data-testid="auth-container"
        >
          <h2 className="font-serif font-light text-2xl text-[#F8F5F2] text-center mb-6">
            {isLogin ? c.welcomeBack : c.beginJourney}
          </h2>

          {/* In-App Browser Warning */}
          {isInAppBrowser && (
            <div className="mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <p className="text-amber-300 font-sans text-sm text-center mb-3">
                {language === 'tr' 
                  ? 'Google ile giriş yapmak için Safari\'de açın' 
                  : 'Open in Safari for Google sign-in'}
              </p>
              <button
                onClick={() => {
                  // Try to open in system browser
                  window.open(window.location.href, '_system');
                }}
                className="w-full py-2.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 font-sans text-sm text-center hover:bg-amber-500/30 transition-all"
                data-testid="open-in-browser-btn"
              >
                {language === 'tr' ? 'Safari\'de Aç' : 'Open in Safari'}
              </button>
              <p className="text-[#6B7280] font-sans text-xs text-center mt-2">
                {language === 'tr' 
                  ? 'veya aşağıdaki e-posta ile giriş yapın' 
                  : 'or use email sign-in below'}
              </p>
            </div>
          )}

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleGoogleSignIn();
            }}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 rounded-full bg-white text-gray-800 px-8 py-3.5 hover:bg-gray-100 active:bg-gray-200 transition-all duration-300 font-sans font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
            data-testid="google-signin-button"
          >
            {googleLoading ? (
              <span>{language === 'tr' ? 'Giriş yapılıyor...' : 'Signing in...'}</span>
            ) : !googleClientId ? (
              <span>{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</span>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {c.googleCta}
              </>
            )}
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0a0a0f]/90 text-[#6B7280] font-sans text-xs uppercase tracking-wider">
                {c.orDivider}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {!isLogin && (
              <div>
                <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                  {t('auth.name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-black/40 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-[#F8F5F2] font-sans focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/50 focus:outline-none transition-all"
                  required
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-black/40 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-[#F8F5F2] font-sans focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/50 focus:outline-none transition-all"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-black/40 border border-[#D4AF37]/20 rounded-xl px-4 py-3 pr-12 text-[#F8F5F2] font-sans focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/50 focus:outline-none transition-all"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#D4AF37] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setForgotPasswordEmail(formData.email);
                  }}
                  className="mt-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300 text-sm font-sans"
                  data-testid="forgot-password-link"
                >
                  {c.forgotPassword}
                </button>
              )}
            </div>

            {/* Keep me signed in checkbox - only show on login */}
            {isLogin && (
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-[#D4AF37]/30 bg-black/40 text-[#D4AF37] focus:ring-[#D4AF37]/50 focus:ring-offset-0 cursor-pointer"
                  data-testid="remember-me-checkbox"
                />
                <label 
                  htmlFor="rememberMe" 
                  className="text-[#A1A1AA] text-sm font-sans cursor-pointer select-none"
                >
                  {c.keepSignedIn}
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-8 py-3.5 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.15)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] font-sans font-medium disabled:opacity-50"
              data-testid="auth-submit-button"
            >
              {loading ? t('auth.pleaseWait') : (isLogin ? c.signIn : c.createAccount)}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300 text-sm font-sans"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? c.needAccount : c.haveAccount}
            </button>
          </div>
        </motion.div>

        {/* Privacy Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-6"
        >
          <button
            onClick={() => navigate('/privacy')}
            className="text-[#6B7280] hover:text-[#D4AF37] transition-colors duration-300 text-xs font-sans"
            data-testid="privacy-link"
          >
            {c.privacy}
          </button>
        </motion.div>
      </motion.div>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => {
              setShowForgotPassword(false);
              setForgotPasswordSent(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="glass-panel rounded-2xl p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="forgot-password-modal"
            >
              {!forgotPasswordSent ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4 border border-[#D4AF37]/30">
                      <Mail className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    <h2 className="font-serif font-light text-2xl text-[#F8F5F2] mb-2">
                      {c.forgotPasswordTitle}
                    </h2>
                    <p className="text-[#A1A1AA] text-sm">
                      {c.forgotPasswordDesc}
                    </p>
                  </div>

                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <div>
                      <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                        {t('auth.email')}
                      </label>
                      <input
                        type="email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        className="w-full bg-black/40 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-[#F8F5F2] font-sans focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/50 focus:outline-none transition-all"
                        placeholder={language === 'tr' ? 'ornek@email.com' : 'you@example.com'}
                        required
                        data-testid="forgot-password-email-input"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={forgotPasswordLoading}
                      className="w-full flex items-center justify-center gap-2 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-8 py-3.5 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.15)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] font-sans font-medium disabled:opacity-50"
                      data-testid="forgot-password-submit"
                    >
                      {forgotPasswordLoading ? (language === 'tr' ? 'Gönderiliyor...' : 'Sending...') : c.sendResetLink}
                      {!forgotPasswordLoading && <ArrowRight className="w-4 h-4" />}
                    </button>
                  </form>

                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="mt-4 w-full flex items-center justify-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300 text-sm font-sans"
                    data-testid="back-to-login"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {c.backToLogin}
                  </button>
                </>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4 border border-green-500/30">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h2 className="font-serif font-light text-2xl text-[#F8F5F2] mb-2">
                    {c.emailSent}
                  </h2>
                  <p className="text-[#A1A1AA] text-sm mb-4">
                    {c.emailSentDesc}
                  </p>
                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3 mb-6">
                    <p className="text-[#D4AF37] text-xs font-medium">
                      {language === 'tr' 
                        ? '⚠️ E-postayı göremiyorsanız spam/önemsiz klasörünüzü kontrol edin' 
                        : '⚠️ Can\'t find the email? Check your spam/junk folder'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordSent(false);
                    }}
                    className="w-full flex items-center justify-center gap-2 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-8 py-3.5 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 font-sans font-medium"
                    data-testid="close-forgot-password"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {c.backToLogin}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Auth;
